import {Component} from "@angular/core";
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {WebmateOptions} from "@webmate-web-core/webmate-options";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {AuthApi} from '@webmate-web-core/shared/api/rest/auth.restapi';
import {map, mergeMap} from 'rxjs/operators';
import {query} from '@angular/animations';
import {AuthStorageStrategy, AuthToken} from '@webmate-web-core/shared/services/auth.service';


/**
 * This component controls what is shown in the SSO Helper pop-up that is opened when someone
 * tries to log in via SSO. It activates the OIDC workflow at webmate. The enclosing window
 * is redirected to the IdP authentication form. The IdP redirects again to this component, setting
 * a query parameter with an OIDC authentication code. The SSO Helper uses this authentication code
 * to request a webmate bearer token (at the webmate API). Before webmate issues the bearer token,
 * it might be necessary to set up the user and its role assignments, which might take some time.
 * This component should render a nice message in the meantime.
 *
 * I have tried to use as few webmate components as possible in this component. Maybe,
 * we will decide later to make this pop-up an autonomous web app (to reduce bundle size).
 */

@Component({
    selector: 'wm-sso-helper',
    styleUrls: ['./sso-helper.component.less'],
    templateUrl: './sso-helper.component.html'
})
export class SSOHelperComponent {
    settingUpUser: boolean = false;
    setupCompleted: boolean = false;
    setupFailed: boolean = false;

    migrateUser: boolean = false;

    // if set, phase 1 of SSO has failed
    ssoInitiationFailure: string;

    private static readonly PHASE_1_URL = "/auth/oidc/initiate-authrequest";
    private static readonly PHASE_2_URL = "/auth/oidc/validate-authcode";

    constructor(private route: ActivatedRoute, private router: Router, private webmateOptions: WebmateOptions,
                private http: HttpClient, private authApi: AuthApi, private authStorageStrategy: AuthStorageStrategy) {
    }


    ngOnInit() {
        this.route.queryParamMap.pipe(mergeMap(queryParams => this.route.fragment.pipe(map(fragment => {
            let mergedParams = new URLSearchParams(fragment || "")
            queryParams.keys.forEach( key => {
                mergedParams.append(key, queryParams.get(key) || "");
            })
            return mergedParams;
        })))).subscribe(params => {
            this.migrateUser = !!params.get('migrateUser');
            let phase = (params.get('code') || params.get('id_token')) ? "processAuthToken" : "initAuthRequest";

            if (phase === 'initAuthRequest') {
                // phase 1: webmate-ui has opened the window and we request an auth request at webmate. webmate will redirect the host window.
                let realm = params.get('realm');
                if (!realm) {
                    throw new Error("Phase 1 must only be called with a valid 'realm' parameter");
                }
                let requestOptions = {};
                if (!this.migrateUser) {
                    requestOptions = {headers: this.authApi.getUnauthorizedHeader().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')};
                } else {
                    requestOptions = {headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')}
                }
                this.http.post(SSOHelperComponent.PHASE_1_URL,
                    `realm=${realm}&client_id=webmate-web-ui`, requestOptions).subscribe(result => {
                        if (result['errorType'] === 'ssoDisabled') {
                            this.ssoInitiationFailure = "Cannot log in. SSO is disabled for this organisation."
                        } else {
                            window.location.href = result['uri'];
                        }
                }, error => {
                       this.setupFailed = true;
                });
            } else if (phase === 'processAuthToken') {
                // phase 2: the IdP, e.g. keycloak, has again redirected to us.
                // IdP must provide the following parameters: state (represents the original request), authcode (the OIDC authcode), nonce (the nonce)
                let body = ""

                let authCode = params.get("code");
                let idToken = params.get("id_token")
                let state = params.get("state");

                this.settingUpUser = true;

                if (!state) {
                    throw new Error("Required parameters for phase 2 missing: state");
                }

                if (authCode) {
                    body = `code=${authCode}&state=${state}`;
                } else if (idToken) {
                    body = `id_token=${idToken}&state=${state}`;
                } else {
                    throw new Error("Required parameters for phase 2 missing: code or access_token");
                }

                this.http.post<AuthToken>(
                    SSOHelperComponent.PHASE_2_URL,
                    body,
                    {headers: this.authApi.getUnauthorizedHeader().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')} ).subscribe(result => {
                    this.notifyAuthInfo(result);
                    this.settingUpUser = false;
                    this.setupCompleted = true;
                    // longer timeout after migration so the user can read the message
                    setTimeout(() => window.close(), this.migrateUser ? 5000 : 2000);
                }, error => {
                    this.settingUpUser = false;
                    this.setupFailed = true;
                });
            } else {
                throw new Error("'phase' parameter is wrong or missing");
            }
        });
    }

    private notifyAuthInfo(authInfo: AuthToken) {
        this.authStorageStrategy.storeAuthInfo(authInfo, true)
    }
}
